import logger from '../utils/logger';
import { BehaviorSubject } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { getLocations } from '../apis/locations';
import { useEffect, useRef, useState } from 'react';
import {
  LocationFinderAggsKey,
  LocationFinderOptions,
  LocationFinderResponse,
} from '../types/location';

// The location data identifiers to fetch data for. Example: ['state', 'city'] or ['state']
interface UseLocationAggregatorProps {
  locationIdentifiers: [string] | [string, string];
  filter?: LocationsFilter;
}

type LocationsFilter = { states: string[] };

const defaultStorageKey = 'all';
const isLoading$ = new BehaviorSubject<Map<string, boolean>>(new Map<string, boolean>());
const data$ = new BehaviorSubject<Map<string, LocationFinderResponse>>(
  new Map<string, LocationFinderResponse>(),
);

const ongoingFetches = new Set<string | LocationsFilter>();

// This function fetches location data based on the provided filter and storage key.
const loadAggregationData = async (storageKey: string, filter?: LocationsFilter) => {
  if (data$.getValue().has(storageKey) || ongoingFetches.has(storageKey)) {
    return;
  }

  try {
    ongoingFetches.add(storageKey);
    isLoading$.next(cloneDeep(isLoading$.getValue()).set(storageKey, true));
    const opts: LocationFinderOptions = {
      aggs: [LocationFinderAggsKey.LOCATION_STATE_CITY],
      filter: {
        states: filter?.states,
      },
    };
    const response = await getLocations(opts);
    const next = cloneDeep(data$.getValue());
    next.set(storageKey, response);
    data$.next(next);
  } catch (error) {
    logger('error', 'at useLocationAggregator:loadAggregationData', error);
  } finally {
    ongoingFetches.delete(storageKey);
    isLoading$.next(cloneDeep(isLoading$.getValue()).set(storageKey, false));
  }
};

// This custom hook is used to manage the state of location data and loading status.
const useLocationAggregator = ({ locationIdentifiers, filter }: UseLocationAggregatorProps) => {
  const storageKeyRef = useRef<string | LocationsFilter>(filter || defaultStorageKey);
  const [count, setCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(
    isLoading$.getValue().get(storageKeyRef.current.toString()) || false,
  );

  useEffect(() => {
    const loadingStateSubscription = isLoading$.subscribe((loadingStateMap) => {
      setIsLoading(loadingStateMap.get(storageKeyRef.current.toString()) || false);
    });

    const aggregateDataSubscription = data$.subscribe((aggregateDataMap) => {
      if (
        !aggregateDataMap.get(storageKeyRef.current.toString())?.aggs?.[
          LocationFinderAggsKey.LOCATION_STATE_CITY
        ]
      ) {
        return setCount(null);
      }

      const { buckets } =
        aggregateDataMap.get(storageKeyRef.current.toString())?.aggs?.[
          LocationFinderAggsKey.LOCATION_STATE_CITY
        ] || {};
      const stateBucket = buckets?.find(({ key }) => key === locationIdentifiers[0]);

      if (!stateBucket) {
        return setCount(null);
      }

      const nextCount =
        locationIdentifiers.length === 1
          ? stateBucket.count ?? null
          : stateBucket.buckets?.find(({ key }) => key === locationIdentifiers[1])?.count ?? null;

      setCount(nextCount);
    });

    return () => {
      loadingStateSubscription.unsubscribe();
      aggregateDataSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    storageKeyRef.current = filter || defaultStorageKey;
    loadAggregationData(storageKeyRef.current.toString(), filter);
  }, [locationIdentifiers, filter]);

  return {
    count,
    isLoading,
  };
};

export default useLocationAggregator;
